<script lang="ts">
	import { Download, Tag, Globe } from 'lucide-svelte';
	import { trackTrailClick } from '$lib/utils/analytics';

	export let trail: {
		id: string;
		name: string;
		description: string;
		latestVersion?: string | null;
		downloadCount: number;
		organization: {
			tag: string;
			name: string;
		};
		tags?: string[];
		origins?: string[];
		updatedAt: string;
	};

	// Function to get favicon URL for a domain
	function getFaviconUrl(domain: string): string {
		return `/api/proxy/favicon?domain=${encodeURIComponent(domain)}`;
	}

	// Handle image error
	function handleImageError(event: Event): void {
		const imgElement = event.target as HTMLImageElement;
		if (imgElement) {
			imgElement.classList.add('hidden');
			const nextElement = imgElement.nextElementSibling as HTMLElement;
			if (nextElement) {
				nextElement.classList.remove('hidden');
			}
		}
	}

	function handleTrailClick() {
		trackTrailClick(trail.name, trail.organization.tag);
	}
</script>

<a
	href={`/trails/@${trail.organization.tag}/${trail.name.split('/')[1]}`}
	class="block w-full"
	on:click={handleTrailClick}
>
	<div
		class="bg-surface-2 rounded-lg p-2 sm:p-3 border border-surface-3 h-full flex flex-col overflow-hidden"
	>
		<div class="flex-grow w-full overflow-hidden">
			<h3 class="text-base font-semibold text-gray-100 mb-1 truncate">
				{trail.name}
			</h3>

			<div class="text-xs text-gray-400 mb-2 flex items-center overflow-hidden">
				<span class="truncate">@{trail.organization.tag}</span>
				{#if trail.latestVersion}
					<span class="mx-2 flex-shrink-0">•</span>
					<span class="flex-shrink-0">v{trail.latestVersion}</span>
				{/if}
			</div>

			<p class="text-sm text-gray-300 mb-3 line-clamp-2 overflow-hidden">
				{trail.description}
			</p>

			{#if trail.tags && trail.tags.length > 0}
				<div class="flex flex-wrap gap-1 mb-2 overflow-hidden">
					{#each trail.tags.slice(0, 1) as tag}
						<div
							class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-900 text-primary-300 max-w-full overflow-hidden"
						>
							<Tag class="w-3 h-3 mr-1 flex-shrink-0" />
							<span class="truncate max-w-[120px]">{tag}</span>
						</div>
					{/each}
					{#if trail.tags.length > 1}
						<div
							class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface-3 text-gray-400 flex-shrink-0"
						>
							+{trail.tags.length - 1}
						</div>
					{/if}
				</div>
			{/if}

			{#if trail.origins && trail.origins.length > 0}
				<div class="flex flex-wrap gap-1 mb-2 overflow-hidden">
					{#each trail.origins.slice(0, 1) as origin}
						<a
							href={`/trails/origins/${origin}`}
							class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-900 text-accent-300 max-w-full overflow-hidden hover:bg-accent-800"
						>
							<div
								class="w-3 h-3 mr-1 flex-shrink-0 overflow-hidden flex items-center justify-center"
							>
								<img
									src={getFaviconUrl(origin)}
									alt=""
									class="w-full h-full object-contain"
									on:error={handleImageError}
								/>
								<Globe class="w-3 h-3 hidden" />
							</div>
							<span class="truncate max-w-[120px]">{origin}</span>
						</a>
					{/each}
					{#if trail.origins.length > 1}
						<div
							class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-surface-3 text-gray-400 flex-shrink-0"
						>
							+{trail.origins.length - 1}
						</div>
					{/if}
				</div>
			{/if}
		</div>

		<div
			class="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-surface-3 w-full overflow-hidden"
		>
			<div class="flex items-center flex-shrink-0">
				<Download class="w-3 h-3 mr-1" />
				<span>{trail.downloadCount}</span>
			</div>
			<div class="truncate text-right ml-2">
				{new Date(trail.updatedAt).toLocaleDateString()}
			</div>
		</div>
	</div>
</a>
